import { TZDate } from '@date-fns/tz';
import { AddressRecord, DeliveryWindowDateRecord, DeliveryWindowOutputRecord } from '@navio/nsdk';
import { ApiErrorPage, ApiErrorPageInternal, useIsWithinTailWindBreakpoint } from 'noddi-ui-web';
import { OutsideServiceAreaInfo } from './OutsideServiceAreaInfo';

import { noddiAsync, URLKeys } from 'noddi-async';
import { SelectedSalesItemsCarGrouped } from 'noddi-types';
import { i18nLocaleToDateFnsLocale, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { ReactNode, useId, useState } from 'react';
import { DayHeader } from './DayHeader';
import { DayTabList } from './DayTabList';
import { EarliestDeliveryDialog } from './EarliestDeliveryDialog';
import { GoToDatePicker } from './GoToDatePicker';
import { isOutsideServiceAreaError } from './isOutsideServiceAreaError';
import { LatestDeliveryDialog } from './LatestDeliveryDialog';
import { SliderArrows } from './SliderArrows';
import { TimeWindowGridSkeleton } from './TimeWindowGridSkeleton';
import { TimeWindowPickerFrame } from './TimeWindowPickerFrame';
import { TimeWindowRow } from './TimeWindowRow';
import { BookingTimeWindowPickerTranslations } from './types';
import { useSwipePaging } from './useSwipePaging';
import {
  countBookableTimeWindows,
  getDeliveryDayLabel,
  getDeliveryWindows,
  getFromAndToDate,
  isSameDeliveryWindow,
  isTimeWindowUnavailable,
  TIME_WINDOW_DAYS_PER_PAGE,
  TimeWindowPickerSurface
} from './utils';

type Props = {
  address: AddressRecord;
  cars: SelectedSalesItemsCarGrouped[];
  selectedDeliveryWindow?: { startsAt: string; endsAt: string };
  translations: BookingTimeWindowPickerTranslations;
  onAvailableSelect: (bookingTimeWindow: DeliveryWindowOutputRecord) => void;
  onUnavailableSelect?: (bookingTimeWindow: DeliveryWindowOutputRecord) => void;
  customButton?: ReactNode;
  isInternalApp?: boolean;
  brandDomain: string;
  maxDate: TZDate;
  firstAvailableDate: TZDate;
  firstAvailableReason: string | null;
  initialFromDateTZ?: TZDate;
  surface: TimeWindowPickerSurface;
};

export const BookingTimeWindowPickerContent = ({
  address,
  cars,
  brandDomain,
  selectedDeliveryWindow,
  onAvailableSelect,
  onUnavailableSelect,
  translations,
  maxDate,
  firstAvailableDate,
  firstAvailableReason,
  initialFromDateTZ,
  customButton,
  isInternalApp,
  surface
}: Props) => {
  const { timezone, id: addressId } = address;

  // Below `md` one day shows at a time behind day tabs; from `md` up the page's days sit side by side.
  const showsDayTabs = useIsWithinTailWindBreakpoint('md');
  // Only a phone drops the card: from `md` up the day columns need the card's edge to read as one grid.
  const isOnPage = surface === 'page' && showsDayTabs;
  const dayPanelId = useId();

  const [isEarliestDialogOpen, setIsEarliestDialogOpen] = useState(false);
  const [isLatestDialogOpen, setIsLatestDialogOpen] = useState(false);

  // Determine the initial date for the time picker.
  // It prioritizes the selected delivery window, then the provided initial date,
  // ensuring it's not before the first available date, and defaults to the first available date if necessary.
  const calculateInitialFromDate = () => {
    if (selectedDeliveryWindow?.startsAt) {
      return new TZDate(new Date(selectedDeliveryWindow.startsAt), timezone.value);
    }

    if (initialFromDateTZ) {
      // Ensure the initial date is not before the first available date to prevent navigation issues
      return initialFromDateTZ < firstAvailableDate ? firstAvailableDate : initialFromDateTZ;
    }

    return firstAvailableDate;
  };

  const [date, setDate] = useState(calculateInitialFromDate);

  const [page, setPage] = useState(0);

  // The day tab the customer picked. Null until they pick one, and again after paging: a new page
  // opens on the day of the selected window, else its first day with anything bookable.
  const [pickedDay, setPickedDay] = useState<string | null>(null);

  const { fromDate, toDate } = getFromAndToDate({
    date,
    page,
    pageSize: TIME_WINDOW_DAYS_PER_PAGE,
    maxDate,
    firstAvailableDate
  });
  const fromDay = noddiFormatDate(fromDate, NoddiDateFormats.API_DATE);
  const toDay = noddiFormatDate(toDate, NoddiDateFormats.API_DATE);

  const {
    error: timeWindowsByDateError,
    isPending: isTimeWindowsByDatePending,
    data: timeWindowsByDate
  } = noddiAsync.useGet({
    type: URLKeys.getAvailableBookingTimeWindowsByDate,
    input: {
      addressId,
      selectedSalesItemIds: cars.map((car) => car.selectedSalesItemIds).flat(),
      fromDate: fromDay,
      toDate: toDay,
      brandDomain
    }
  });

  const onSkip = (skipToDate: TZDate) => {
    setDate(skipToDate);
    setPage(0);
    setPickedDay(null);
  };

  // Compared as calendar days: a page opened on a selected window starts at that window's hour, which
  // is later than the first available date's midnight on the very same day.
  const isAtStart = fromDay <= noddiFormatDate(firstAvailableDate, NoddiDateFormats.API_DATE);
  const isAtEnd = toDay >= noddiFormatDate(maxDate, NoddiDateFormats.API_DATE);

  const goToPreviousPage = () => {
    if (isAtStart) {
      setIsEarliestDialogOpen(true);
    } else {
      setPage(page - 1);
      setPickedDay(null);
    }
  };

  const goToNextPage = () => {
    if (isAtEnd) {
      setIsLatestDialogOpen(true);
    } else {
      setPage(page + 1);
      setPickedDay(null);
    }
  };

  // Swiping past either end does nothing: the dialog explaining why is for a deliberate press of the button.
  const swipePaging = useSwipePaging({
    onSwipeLeft: () => !isAtEnd && goToNextPage(),
    onSwipeRight: () => !isAtStart && goToPreviousPage()
  });

  if (timeWindowsByDateError) {
    if (isOutsideServiceAreaError(timeWindowsByDateError)) {
      return <OutsideServiceAreaInfo />;
    }
    return isInternalApp ? (
      <ApiErrorPageInternal error={timeWindowsByDateError} />
    ) : (
      <ApiErrorPage error={timeWindowsByDateError} />
    );
  }

  const renderRows = (timeWindows: DeliveryWindowOutputRecord[], layout: 'inline' | 'stacked', dateLabel: string) =>
    timeWindows.map((timeWindow) => (
      <TimeWindowRow
        key={timeWindow.deliveryWindowId}
        layout={layout}
        timeWindow={timeWindow}
        dateLabel={dateLabel}
        onAvailableSelect={onAvailableSelect}
        onUnavailableSelect={onUnavailableSelect}
        translations={translations}
        selectedDeliveryWindow={selectedDeliveryWindow}
        isOnPage={isOnPage}
      />
    ));

  const renderDays = () => {
    if (isTimeWindowsByDatePending) {
      return (
        <TimeWindowGridSkeleton
          layout={showsDayTabs ? 'tabs' : 'columns'}
          isOnPage={isOnPage}
          loadingLabel={translations.loading}
        />
      );
    }

    if (!showsDayTabs) {
      // Sold-out windows keep their place in the column, so a time sits on the same row in every day.
      return (
        <div {...swipePaging} className='grid touch-pan-y grid-cols-5 gap-6 select-none'>
          {timeWindowsByDate.map((dateRecord) => (
            <div key={dateRecord.date} className='flex min-w-0 flex-col'>
              <DayHeader dateRecord={dateRecord} translations={translations} />
              <div className='flex flex-col gap-2 pt-3'>
                {renderRows(
                  getDeliveryWindows(dateRecord),
                  'stacked',
                  getDeliveryDayLabel(dateRecord.date, translations)
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    const activeDay = getActiveDay({ days: timeWindowsByDate, pickedDay, selectedDeliveryWindow });
    const activeDayLabel = activeDay ? getDeliveryDayLabel(activeDay.date, translations) : '';
    const activeDayWindows = activeDay ? getDeliveryWindows(activeDay) : [];
    // One day at a time, so the bookable windows go first and sold out gathers at the bottom.
    const bookableWindows = activeDayWindows.filter((timeWindow) => !isTimeWindowUnavailable({ timeWindow }));
    const soldOutWindows = activeDayWindows.filter((timeWindow) => isTimeWindowUnavailable({ timeWindow }));

    return (
      <>
        <DayTabList {...swipePaging} role='tablist' isOnPage={isOnPage} className='touch-pan-y select-none'>
          {timeWindowsByDate.map((dateRecord) => (
            <DayHeader
              key={dateRecord.date}
              dateRecord={dateRecord}
              translations={translations}
              tab={{
                isActive: dateRecord.date === activeDay?.date,
                onSelect: () => setPickedDay(dateRecord.date),
                panelId: dayPanelId,
                isOnPage
              }}
            />
          ))}
        </DayTabList>
        <div id={dayPanelId} role='tabpanel' className='flex flex-col gap-5'>
          {bookableWindows.length > 0 && (
            <div className='flex flex-col gap-2'>{renderRows(bookableWindows, 'inline', activeDayLabel)}</div>
          )}
          {soldOutWindows.length > 0 && (
            <div className='flex flex-col'>{renderRows(soldOutWindows, 'inline', activeDayLabel)}</div>
          )}
        </div>
      </>
    );
  };

  const content = (
    <>
      <EarliestDeliveryDialog
        open={isEarliestDialogOpen}
        onClose={() => setIsEarliestDialogOpen(false)}
        reason={firstAvailableReason}
        firstAvailableDate={firstAvailableDate}
        translations={translations}
      />
      <LatestDeliveryDialog
        open={isLatestDialogOpen}
        onClose={() => setIsLatestDialogOpen(false)}
        latestAvailableDate={maxDate}
        translations={translations}
      />
      <div className='flex items-center justify-between gap-2'>
        {customButton}
        <div className='ml-auto flex min-w-0 items-center'>
          <GoToDatePicker
            skipToDate={date}
            setSkipToDate={onSkip}
            minDate={firstAvailableDate}
            maxDate={maxDate}
            translations={{
              jumpToDateTriggerText: translations.jumpToDateTriggerText
            }}
            timeZone={timezone.value}
          />
        </div>
      </div>
      <SliderArrows
        translations={translations}
        label={getDateRangeLabel({ fromDate, toDate, timeZone: timezone.value })}
        isAtStart={isAtStart}
        isAtEnd={isAtEnd}
        onPrevButtonClick={goToPreviousPage}
        onNextButtonClick={goToNextPage}
      />
      {renderDays()}
    </>
  );

  return <TimeWindowPickerFrame isOnPage={isOnPage}>{content}</TimeWindowPickerFrame>;
};

const getActiveDay = ({
  days,
  pickedDay,
  selectedDeliveryWindow
}: {
  days: DeliveryWindowDateRecord[];
  pickedDay: string | null;
  selectedDeliveryWindow?: { startsAt: string; endsAt: string };
}) =>
  days.find((day) => day.date === pickedDay) ??
  days.find((day) =>
    getDeliveryWindows(day).some((timeWindow) => isSameDeliveryWindow(timeWindow, selectedDeliveryWindow))
  ) ??
  days.find((day) => countBookableTimeWindows(day) > 0) ??
  days[0];

// The page's days in the customer's language: "16.–20. okt.", "30. okt.–3. nov.", "Oct 16 – 20". Intl
// collapses a shared month itself, which date-fns has no format for.
const getDateRangeLabel = ({ fromDate, toDate, timeZone }: { fromDate: Date; toDate: Date; timeZone: string }) =>
  new Intl.DateTimeFormat(i18nLocaleToDateFnsLocale().code, { day: 'numeric', month: 'short', timeZone }).formatRange(
    fromDate,
    toDate
  );
